import type { LocationQuery } from "vue-router";
import {
  COLOR_MODES,
  FONT_SIZE_LEVELS,
  type A11yColorScheme,
  type A11yDisplayPreferences,
  type A11yFontSizeLevel,
} from "@/utils/a11yDisplayPreferences";
import { firstQueryValue } from "@/utils/brokerUrlQuery";
import { localeFromRouteQuery, type AppLocale } from "@/composables/appLocale";

export const COVOMO_SET_DISPLAY_PREFERENCES_TYPE =
  "covomoSetDisplayPreferences";
export const COVOMO_DISPLAY_PREFERENCES_STATE_TYPE =
  "covomoDisplayPreferencesState";

export type EmbedDisplaySettingsInput = {
  lang?: unknown;
  color_mode?: unknown;
  dark_mode?: unknown;
  high_contrast?: unknown;
  contrast?: unknown;
  font_size?: unknown;
};

export type ParsedEmbedDisplaySettings = {
  locale: AppLocale | null;
  displayPrefs: Partial<A11yDisplayPreferences>;
};

function asTrimmedString(v: unknown): string {
  if (v === undefined || v === null) return "";
  return String(v).trim();
}

function parseTruthyFlag(raw: string): boolean | null {
  const v = raw.trim().toLowerCase();
  if (v === "") return null;
  if (v === "1" || v === "true" || v === "yes" || v === "on") return true;
  if (v === "0" || v === "false" || v === "no" || v === "off") return false;
  return null;
}

export function parseColorModeValue(v: unknown): A11yColorScheme | null {
  const raw = asTrimmedString(v).toLowerCase();
  if (!raw) return null;
  if ((COLOR_MODES as readonly string[]).includes(raw)) {
    return raw as A11yColorScheme;
  }
  return null;
}

export function parseHighContrastValue(v: unknown): boolean | null {
  if (typeof v === "boolean") return v;
  const raw = asTrimmedString(v);
  if (!raw) return null;
  return parseTruthyFlag(raw);
}

export function parseFontSizeLevelValue(v: unknown): A11yFontSizeLevel | null {
  if (typeof v === "number" && Number.isFinite(v)) {
    const n = Math.trunc(v);
    if ((FONT_SIZE_LEVELS as readonly number[]).includes(n)) {
      return n as A11yFontSizeLevel;
    }
    return null;
  }
  const raw = asTrimmedString(v);
  if (!raw) return null;
  const n = parseInt(raw, 10);
  if ((FONT_SIZE_LEVELS as readonly number[]).includes(n)) {
    return n as A11yFontSizeLevel;
  }
  return null;
}

function parseLocaleValue(v: unknown): AppLocale | null {
  const raw = asTrimmedString(v).toLowerCase();
  if (!raw) return null;
  return localeFromRouteQuery({ lang: raw });
}

export function parseEmbedDisplaySettings(
  raw: EmbedDisplaySettingsInput,
): ParsedEmbedDisplaySettings {
  const displayPrefs: Partial<A11yDisplayPreferences> = {};

  const colorMode =
    parseColorModeValue(raw.color_mode) ?? parseColorModeValue(raw.dark_mode);
  if (colorMode) displayPrefs.colorScheme = colorMode;

  const highContrast =
    parseHighContrastValue(raw.high_contrast) ??
    parseHighContrastValue(raw.contrast);
  if (highContrast !== null) displayPrefs.highContrast = highContrast;

  const fontSizeLevel = parseFontSizeLevelValue(raw.font_size);
  if (fontSizeLevel !== null) displayPrefs.fontSizeLevel = fontSizeLevel;

  const locale = parseLocaleValue(raw.lang);

  return { locale, displayPrefs };
}

export function parseEmbedDisplaySettingsFromRouteQuery(
  q: LocationQuery,
): ParsedEmbedDisplaySettings {
  const lang = firstQueryValue(q, "lang");
  const colorMode = firstQueryValue(q, "color_mode", "dark_mode");
  const highContrast = firstQueryValue(q, "high_contrast", "contrast");
  const fontSize = firstQueryValue(q, "font_size");

  return parseEmbedDisplaySettings({
    lang: lang || undefined,
    color_mode: colorMode || undefined,
    high_contrast: highContrast || undefined,
    font_size: fontSize || undefined,
  });
}

export function buildCovomoDisplayPreferencesStatePayload(
  locale: AppLocale,
  prefs: A11yDisplayPreferences,
) {
  return {
    type: COVOMO_DISPLAY_PREFERENCES_STATE_TYPE,
    lang: locale,
    color_mode: prefs.colorScheme,
    high_contrast: prefs.highContrast,
    font_size: prefs.fontSizeLevel,
  };
}
